import { getCountries } from "react-phone-number-input";
import labels from "react-phone-number-input/locale/en.json";

export type CountryAnswer = {
  code: string | null;
  name: string;
};

export type CountryOption = CountryAnswer & {
  code: string;
  value: string;
  label: string;
};

const COUNTRY_ALIASES: Record<string, string> = {
  america: "US",
  britain: "GB",
  england: "GB",
  "great britain": "GB",
  "u.k.": "GB",
  uk: "GB",
  "united kingdom": "GB",
  "u.s.": "US",
  "u.s.a.": "US",
  us: "US",
  usa: "US",
  "united states": "US",
  "united states of america": "US",
};

const COUNTRY_LABELS = labels as Record<string, string>;

export function countryFlagEmoji(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return "";
  return Array.from(normalized)
    .map((letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
    .join("");
}

export const COUNTRY_OPTIONS: CountryOption[] = getCountries()
  .map((code) => {
    const name = COUNTRY_LABELS[code] || code;
    const flag = countryFlagEmoji(code);
    return { code, name, value: code, label: flag ? `${flag} ${name}` : name };
  })
  .sort((a, b) => a.name.localeCompare(b.name, "en"));

const COUNTRY_BY_CODE = new Map(
  COUNTRY_OPTIONS.map((country) => [country.code, country]),
);

const COUNTRY_BY_NAME = new Map(
  COUNTRY_OPTIONS.map((country) => [
    country.name.trim().toLowerCase(),
    country,
  ]),
);

export function getCountryByCode(code: string): CountryAnswer | null {
  const country = COUNTRY_BY_CODE.get(code.trim().toUpperCase());
  return country ? { code: country.code, name: country.name } : null;
}

export function normalizeCountryAnswer(value: unknown): CountryAnswer | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "object" && !Array.isArray(value)) {
    const raw = value as Record<string, unknown>;
    const code = String(raw.code ?? "").trim().toUpperCase();
    const name = String(raw.name ?? "").trim();
    if (code) {
      const country = getCountryByCode(code);
      if (country) return country;
    }
    if (name) {
      const byName = COUNTRY_BY_NAME.get(name.toLowerCase());
      if (byName) return { code: byName.code, name: byName.name };
      return { code: null, name };
    }
    return null;
  }

  const raw = String(value).trim();
  if (!raw) return null;
  const aliasCode = COUNTRY_ALIASES[raw.toLowerCase()];
  if (aliasCode) return getCountryByCode(aliasCode);
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const byCode = getCountryByCode(raw);
    if (byCode) return byCode;
  }
  const byName = COUNTRY_BY_NAME.get(raw.toLowerCase());
  if (byName) return { code: byName.code, name: byName.name };
  return { code: null, name: raw };
}

export function countryNameFromAnswer(value: unknown): string {
  return normalizeCountryAnswer(value)?.name ?? "";
}
